import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Dictionary, createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';

export interface LabelProgress {
  id: string;
  batch: number;
  nextToken: string | undefined;
  complete: boolean;
  /**
   * Fingerprint of the query conditions this progress was earned under — sample, creator
   * and score bounds.
   *
   * A `nextToken` is a DynamoDB `ExclusiveStartKey` and is only valid for the exact
   * conditions that produced it; replaying one against a different set is rejected with
   * "the provided starting key is outside query boundaries based on provided conditions".
   * Progress is keyed by label alone, so without recording the conditions there is no way
   * to tell a usable token from a stale one — only to guess from timing, which is what a
   * succession of cancellation guards was doing.
   *
   * `undefined` for a freshly initialised entry, which has no token to qualify.
   */
  conditions?: string;
}

interface MosaicPaginationState {
  labelProgress: EntityState<LabelProgress>;
}

const adapter: EntityAdapter<LabelProgress> = createEntityAdapter<LabelProgress>();

@Injectable()
export class MosaicPaginationStore extends ComponentStore<MosaicPaginationState> {
  constructor() {
    super({ labelProgress: adapter.getInitialState() });
  }

  // #region Selectors

  public readonly labelProgress$ = this.select((state) => state.labelProgress.entities);

  public readonly nextLabel$ = this.select((state) => {
    for (const id of state.labelProgress.ids) {
      if (!state.labelProgress.entities[id]?.complete) return id as string;
    }
    return undefined;
  });

  // #endregion

  // #region Updaters

  public readonly initializeProgress = this.updater(
    (state, payload: { labelIds: string[]; labelCounts: Map<string, number> }): MosaicPaginationState => ({
      ...state,
      labelProgress: adapter.setAll(
        payload.labelIds.map((id) => ({
          id,
          batch: 0,
          complete: false,
          nextToken: undefined,
          conditions: undefined,
        })),
        state.labelProgress
      ),
    })
  );

  /**
   * Drops all progress, tokens included.
   *
   * A `nextToken` is a DynamoDB `ExclusiveStartKey` earned under one set of query
   * conditions — a specific sample and creator — but this store keys progress by LABEL
   * alone. Carrying one into the next generation makes the backend reject the query
   * ("the provided starting key is outside query boundaries"), so a generation change has
   * to discard them rather than wait for the next `initializeProgress`.
   */
  public readonly clearProgress = this.updater(
    (state): MosaicPaginationState => ({
      ...state,
      labelProgress: adapter.removeAll(state.labelProgress),
    })
  );

  public readonly advanceProgress = this.updater(
    (
      state,
      payload: { label: string; previousBatch: number; nextToken: string | undefined; conditions: string }
    ): MosaicPaginationState => ({
      ...state,
      labelProgress: adapter.upsertOne(
        {
          id: payload.label,
          batch: payload.previousBatch + 1,
          nextToken: payload.nextToken,
          complete: !payload.nextToken,
          // Recorded so the next request can tell whether this token is still usable.
          conditions: payload.conditions,
        },
        state.labelProgress
      ),
    })
  );

  // #endregion
}
